class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.abortController = null;

    if (!this.input || !this.predictiveSearchResults) {
      console.warn('PredictiveSearch: Required elements not found');
      return;
    }

    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    // Close on click outside
    document.addEventListener('click', (event) => {
      if (!this.closest('.header-search').contains(event.target)) {
        this.close();
      }
    });
    
    // Close button functionality
    this.addEventListener('click', (event) => {
      if (event.target.closest('.predictive-search__close-btn')) {
        this.close();
      }
    });

    // Handle keyboard navigation
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    // Update footer link with current search term
    this.updateFooterLink(searchTerm);
    
    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    
    this.abortController = new AbortController();
    
    /* Use localized suggest URL so Liquid renders product/url with current language prefix (e.g. /es/). */
    const rootPrefix =
      typeof window.routes !== 'undefined' && window.routes.rootUrl != null
        ? String(window.routes.rootUrl).replace(/\/+$/, '')
        : '';
    const suggestPath =
      `${rootPrefix}/search/suggest?q=` +
      `${encodeURIComponent(searchTerm)}` +
      '&resources[type]=product,page,article,collection&resources[limit]=24' +
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last' +
      '&section_id=predictive-search';
    const url = suggestPath.startsWith('/') ? suggestPath : `/${suggestPath}`;
    
    fetch(url, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.show();
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('PredictiveSearch error:', error);
          this.close();
        }
      });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const firstResult = this.predictiveSearchResults.querySelector('.predictive-search__link');
      if (firstResult) firstResult.focus();
    }
  }

  show() {
    this.predictiveSearchResults.style.display = 'block';
    // Force reflow to ensure display:block is applied before transition
    this.predictiveSearchResults.offsetHeight;
    this.predictiveSearchResults.classList.add('is-visible');
    this.input.setAttribute('aria-expanded', 'true');
    
    // Show the external footer
    const footer = document.querySelector('.predictive-search__footer');
    if (footer) {
      footer.classList.add('is-visible');
    }
  }

  close() {
    this.predictiveSearchResults.classList.remove('is-visible');
    
    // Hide the external footer
    const footer = document.querySelector('.predictive-search__footer');
    if (footer) {
      footer.classList.remove('is-visible');
    }
    
    // Wait for transition to complete before hiding
    setTimeout(() => {
      this.predictiveSearchResults.style.display = 'none';
      this.predictiveSearchResults.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
    }, 300); // Match CSS transition duration
  }

  updateFooterLink(searchTerm) {
    const footer = document.querySelector('.predictive-search__footer');
    const footerLink = footer?.querySelector('a');
    
    if (footerLink && searchTerm) {
      const tpl =
        typeof window.languages !== 'undefined' &&
        typeof window.languages.predictiveSearchForTerms === 'string' &&
        window.languages.predictiveSearchForTerms.length
          ? window.languages.predictiveSearchForTerms
          : 'Search for "{terms}"';
      footerLink.textContent = tpl.replace(/\{terms\}/g, searchTerm);

      // Update the link URL
      const base =
        typeof window.routes !== 'undefined' && window.routes.searchUrl != null
          ? String(window.routes.searchUrl).split('?')[0]
          : '/search';
      const searchUrl = `${base}?q=${encodeURIComponent(searchTerm)}&type=product,page,article`;
      footerLink.href = searchUrl.startsWith('/') ? searchUrl : `/${searchUrl}`;
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);